import React from "react";

/* ═══════════════════════════════════════════════════════════════
   Transport & Vehicles — 8 ANIMATED templates
   airplane_cross, car_dashboard, bicycle_mechanics,
   train_system, submarine_dive, helicopter_rotor,
   sailboat_wind, hot_air_balloon
   ═══════════════════════════════════════════════════════════════ */

/* ─── 1. Airplane Cross Section ───────────────────────────── */
export const AirplaneCross: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "How Planes Fly";
  const phase = (props.phase as number) || 0;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        <linearGradient id="ac-sky" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#42a5f5" /><stop offset="100%" stopColor="#0d47a1" />
        </linearGradient>
      </defs>
      <rect width="800" height="600" fill="url(#ac-sky)" rx="16" />
      {/* Moving clouds */}
      {[{ x: 100, y: 80, s: 1 }, { x: 400, y: 450, s: 0.6 }, { x: 650, y: 120, s: 0.8 }].map((c, i) => (
        <ellipse key={i} cx={c.x} cy={c.y} rx={60 * c.s} ry={25 * c.s} fill="#ecf0f1" opacity="0.15">
          <animate attributeName="cx" from={c.x + 400} to={c.x - 400} dur={`${12 + i * 4}s`} repeatCount="indefinite" />
        </ellipse>
      ))}
      {/* Fuselage */}
      <ellipse cx="400" cy="280" rx="220" ry="45" fill="#bdbdbd" stroke="#9e9e9e" strokeWidth="2" />
      {/* Cockpit */}
      <ellipse cx="620" cy="275" rx="30" ry="20" fill="#42a5f5" opacity="0.5" stroke="#9e9e9e" strokeWidth="1" />
      {/* Wings — airfoil shape */}
      <path d="M300,280 L250,240 Q300,230 400,240 L500,245 L500,280" fill="#9e9e9e" opacity="0.8" />
      <path d="M300,280 L250,320 Q300,330 400,320 L500,315 L500,280" fill="#9e9e9e" opacity="0.7" />
      {/* Engine */}
      <ellipse cx="310" cy="240" rx="25" ry="12" fill="#546e7a" stroke="#455a64" strokeWidth="1" />
      {/* Exhaust animated */}
      {[0, 1, 2].map((i) => (
        <ellipse key={i} cx={285 - i * 25} cy="240" rx={10 + i * 6} ry={5 + i * 3} fill="#bdbdbd" opacity={0.15 - i * 0.04}>
          <animate attributeName="cx" from={285 - i * 25} to={185 - i * 25} dur="2s" repeatCount="indefinite" />
          <animate attributeName="opacity" values={`${0.15 - i * 0.04};0`} dur="2s" repeatCount="indefinite" />
        </ellipse>
      ))}
      {/* Tail */}
      <polygon points="180,270 140,220 180,235" fill="#bdbdbd" stroke="#9e9e9e" strokeWidth="1" />
      {/* Airflow lines */}
      {[200, 260, 320].map((y, i) => (
        <path key={i} d={`M${650},${y} Q${550},${y - 10 + Math.sin(phase + i) * 5} ${450},${y}`}
          fill="none" stroke="#ecf0f1" strokeWidth="1" opacity="0.2" strokeDasharray="8,6">
          <animate attributeName="stroke-dashoffset" from="14" to="0" dur="0.6s" repeatCount="indefinite" />
        </path>
      ))}
      {/* Lift / Thrust arrows */}
      <g opacity="0.6">
        <line x1="400" y1="230" x2="400" y2="170" stroke="#4caf50" strokeWidth="3" markerEnd="url(#ac-up)" />
        <text x="420" y="185" fill="#4caf50" fontSize="12" fontWeight="600">Lift</text>
        <line x1="630" y1="280" x2="690" y2="280" stroke="#ff9800" strokeWidth="3" markerEnd="url(#ac-rt)" />
        <text x="670" y="270" fill="#ff9800" fontSize="12" fontWeight="600">Thrust</text>
        <line x1="170" y1="280" x2="120" y2="280" stroke="#e74c3c" strokeWidth="3" markerEnd="url(#ac-lt)" />
        <text x="120" y="270" fill="#e74c3c" fontSize="12" fontWeight="600">Drag</text>
      </g>
      <defs>
        <marker id="ac-up" viewBox="0 0 10 10" refX="5" refY="10" markerWidth="6" markerHeight="6" orient="auto"><path d="M0,10 L5,0 L10,10Z" fill="#4caf50" /></marker>
        <marker id="ac-rt" viewBox="0 0 10 10" refX="10" refY="5" markerWidth="6" markerHeight="6" orient="auto"><path d="M0,0 L10,5 L0,10Z" fill="#ff9800" /></marker>
        <marker id="ac-lt" viewBox="0 0 10 10" refX="0" refY="5" markerWidth="6" markerHeight="6" orient="auto"><path d="M10,0 L0,5 L10,10Z" fill="#e74c3c" /></marker>
      </defs>
      <text x="400" y="500" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 2. Car Dashboard ────────────────────────────────────── */
export const CarDashboard: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Car Dashboard";
  const rotation = (props.rotation as number) || 0;
  const fillPercent = (props.fillPercent as number) ?? 65;
  const speedValue = (props.speedValue as number) ?? fillPercent;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Dashboard panel */}
      <rect x="50" y="80" width="700" height="420" rx="30" fill="#1c2833" stroke="#2c3e50" strokeWidth="3" />
      {/* Speedometer */}
      <g transform="translate(260,300)">
        <circle r="130" fill="#161b22" stroke="#2c3e50" strokeWidth="3" />
        {/* Speed marks */}
        {Array.from({ length: 13 }, (_, i) => {
          const angle = (-210 + i * 20) * Math.PI / 180;
          return (
            <g key={i}>
              <line x1={Math.cos(angle) * 105} y1={Math.sin(angle) * 105}
                x2={Math.cos(angle) * 120} y2={Math.sin(angle) * 120}
                stroke="#546e7a" strokeWidth="2" />
              <text x={Math.cos(angle) * 90} y={Math.sin(angle) * 90 + 4}
                textAnchor="middle" fill="#7f8c8d" fontSize="10">{i * 20}</text>
            </g>
          );
        })}
        {/* Needle */}
        {(() => {
          const needleAngle = (-210 + (Math.min(100, Math.max(0, speedValue)) / 100) * 240) * Math.PI / 180;
          return (
            <line
              x1="0"
              y1="0"
              x2={Math.cos(needleAngle) * 95}
              y2={Math.sin(needleAngle) * 95}
              stroke="#e74c3c"
              strokeWidth="3"
              strokeLinecap="round"
            />
          );
        })()}
        <circle r="8" fill="#e74c3c" />
        <text x="0" y="50" textAnchor="middle" fill="#ecf0f1" fontSize="18" fontWeight="700">km/h</text>
      </g>
      {/* RPM gauge */}
      <g transform="translate(560,300)">
        <circle r="90" fill="#161b22" stroke="#2c3e50" strokeWidth="2" />
        {Array.from({ length: 9 }, (_, i) => {
          const angle = (-210 + i * 30) * Math.PI / 180;
          return (
            <line key={i} x1={Math.cos(angle) * 72} y1={Math.sin(angle) * 72}
              x2={Math.cos(angle) * 82} y2={Math.sin(angle) * 82}
              stroke={i > 6 ? "#e74c3c" : "#546e7a"} strokeWidth="2" />
          );
        })}
        {/* RPM needle */}
        {(() => {
          const rpmAngle = -210 + (rotation % 360) / 360 * 240;
          const rad = rpmAngle * Math.PI / 180;
          return <line x1="0" y1="0" x2={Math.cos(rad) * 65} y2={Math.sin(rad) * 65} stroke="#f9a825" strokeWidth="2" strokeLinecap="round" />;
        })()}
        <circle r="5" fill="#f9a825" />
        <text x="0" y="35" textAnchor="middle" fill="#bdc3c7" fontSize="12">RPM</text>
      </g>
      {/* Fuel gauge */}
      <rect x="160" y="440" width="120" height="20" rx="4" fill="#161b22" stroke="#2c3e50" strokeWidth="1" />
      <rect x="162" y="442" width={Math.min(116, fillPercent * 1.16)} height="16" rx="3"
        fill={fillPercent > 30 ? "#4caf50" : "#e74c3c"}>
        <animate attributeName="opacity" values="0.8;1;0.8" dur="2s" repeatCount="indefinite" />
      </rect>
      <text x="145" y="455" fill="#7f8c8d" fontSize="10">⛽</text>
      <text x="400" y="550" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 3. Bicycle Mechanics ────────────────────────────────── */
export const BicycleMechanics: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Bicycle";
  const rotation = (props.rotation as number) || 0;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Rear wheel */}
      <g transform={`rotate(${rotation}, 250, 380)`}>
        <circle cx="250" cy="380" r="100" fill="none" stroke="#546e7a" strokeWidth="4" />
        {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => {
          const rad = a * Math.PI / 180;
          return <line key={a} x1="250" y1="380" x2={250 + Math.cos(rad) * 96} y2={380 + Math.sin(rad) * 96} stroke="#455a64" strokeWidth="1.5" />;
        })}
        <circle cx="250" cy="380" r="10" fill="#546e7a" />
      </g>
      {/* Front wheel */}
      <g transform={`rotate(${rotation}, 550, 380)`}>
        <circle cx="550" cy="380" r="100" fill="none" stroke="#546e7a" strokeWidth="4" />
        {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => {
          const rad = a * Math.PI / 180;
          return <line key={a} x1="550" y1="380" x2={550 + Math.cos(rad) * 96} y2={380 + Math.sin(rad) * 96} stroke="#455a64" strokeWidth="1.5" />;
        })}
        <circle cx="550" cy="380" r="10" fill="#546e7a" />
      </g>
      {/* Frame */}
      <line x1="250" y1="380" x2="400" y2="230" stroke="#e74c3c" strokeWidth="5" />
      <line x1="400" y1="230" x2="550" y2="380" stroke="#e74c3c" strokeWidth="5" />
      <line x1="250" y1="380" x2="380" y2="380" stroke="#e74c3c" strokeWidth="5" />
      <line x1="400" y1="230" x2="380" y2="380" stroke="#e74c3c" strokeWidth="5" />
      {/* Seat */}
      <line x1="380" y1="380" x2="370" y2="230" stroke="#e74c3c" strokeWidth="4" />
      <rect x="350" y="218" width="50" height="10" rx="4" fill="#3e2723" />
      {/* Handlebars */}
      <line x1="550" y1="380" x2="560" y2="210" stroke="#e74c3c" strokeWidth="4" />
      <rect x="540" y="200" width="40" height="8" rx="4" fill="#1c2833" />
      {/* Chain + pedals */}
      <circle cx="380" cy="380" r="25" fill="none" stroke="#f9a825" strokeWidth="2" />
      {/* Pedal crank animated */}
      <g transform={`rotate(${rotation}, 380, 380)`}>
        <line x1="380" y1="380" x2="380" y2="410" stroke="#bdbdbd" strokeWidth="3" />
        <rect x="372" y="408" width="16" height="6" rx="2" fill="#bdbdbd" />
      </g>
      {/* Ground */}
      <line x1="80" y1="482" x2="720" y2="482" stroke="#2c3e50" strokeWidth="2" />
      <text x="400" y="545" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 4. Train System ─────────────────────────────────────── */
export const TrainSystem: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Train";
  const animationProgress = (props.animationProgress as number) || 0;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Track */}
      <line x1="0" y1="430" x2="800" y2="430" stroke="#5d4037" strokeWidth="8" />
      <line x1="0" y1="440" x2="800" y2="440" stroke="#5d4037" strokeWidth="8" />
      {/* Sleepers */}
      {Array.from({ length: 20 }, (_, i) => (
        <rect key={i} x={i * 42} y="425" width="30" height="20" fill="#3e2723" />
      ))}
      {/* Train body moving */}
      <g transform={`translate(${-200 + animationProgress * 900}, 0)`}>
        {/* Locomotive */}
        <rect x="100" y="340" width="180" height="85" rx="8" fill="#1565c0" stroke="#0d47a1" strokeWidth="2" />
        <rect x="110" y="350" width="40" height="30" rx="4" fill="#42a5f5" opacity="0.6" />
        <rect x="160" y="350" width="40" height="30" rx="4" fill="#42a5f5" opacity="0.6" />
        {/* Chimney */}
        <rect x="240" y="310" width="20" height="30" fill="#546e7a" />
        {/* Smoke */}
        {[0, 1, 2].map((i) => (
          <circle key={i} cx={252} cy={296 - i * 20} r={8 + i * 5}
            fill="#757575" opacity={0.3 - i * 0.08}>
            <animate attributeName="cy" from={296 - i * 20} to={240 - i * 30} dur={`${1.5 + i * 0.5}s`} repeatCount="indefinite" />
            <animate attributeName="opacity" values={`${0.3 - i * 0.08};0`} dur={`${1.5 + i * 0.5}s`} repeatCount="indefinite" />
          </circle>
        ))}
        {/* Wheels */}
        {[130, 180, 240].map((x) => (
          <g key={x}>
            <circle cx={x} cy="425" r="18" fill="#1c2833" stroke="#bdbdbd" strokeWidth="3">
              <animateTransform attributeName="transform" type="rotate" from={`0 ${x} 425`} to={`360 ${x} 425`} dur="0.8s" repeatCount="indefinite" />
            </circle>
            <circle cx={x} cy="425" r="4" fill="#bdbdbd" />
          </g>
        ))}
        {/* Carriages */}
        {[0, 1].map((c) => (
          <g key={c}>
            <rect x={-60 - c * 160} y="350" width="140" height="75" rx="6" fill="#e74c3c" stroke="#c62828" strokeWidth="2" />
            {[0, 1, 2].map((w) => (
              <rect key={w} x={-50 - c * 160 + w * 42} y="360" width="30" height="25" rx="3" fill="#42a5f5" opacity="0.4" />
            ))}
            {[-20 - c * 160, 40 - c * 160].map((wx) => (
              <circle key={wx} cx={wx} cy="425" r="14" fill="#1c2833" stroke="#bdbdbd" strokeWidth="2">
                <animateTransform attributeName="transform" type="rotate" from={`0 ${wx} 425`} to={`360 ${wx} 425`} dur="0.8s" repeatCount="indefinite" />
              </circle>
            ))}
          </g>
        ))}
      </g>
      {/* Landscape */}
      {[150, 600].map((x) => (
        <polygon key={x} points={`${x - 20},430 ${x},370 ${x + 20},430`} fill="#2e7d32" opacity="0.3" />
      ))}
      <text x="400" y="520" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 5. Submarine Dive ───────────────────────────────────── */
export const SubmarineDive: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Submarine";
  const animationProgress = (props.animationProgress as number) || 0;
  const depth = animationProgress * 350;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        <linearGradient id="sd-ocean" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#42a5f5" /><stop offset="50%" stopColor="#1565c0" /><stop offset="100%" stopColor="#0a1628" />
        </linearGradient>
      </defs>
      <rect width="800" height="600" fill="url(#sd-ocean)" rx="16" />
      {/* Surface waves */}
      <path d="M0,50 Q100,40 200,50 Q300,60 400,50 Q500,40 600,50 Q700,60 800,50" fill="none" stroke="#90caf9" strokeWidth="2" opacity="0.3">
        <animate attributeName="d" values="M0,50 Q100,40 200,50 Q300,60 400,50 Q500,40 600,50 Q700,60 800,50;M0,50 Q100,60 200,50 Q300,40 400,50 Q500,60 600,50 Q700,40 800,50;M0,50 Q100,40 200,50 Q300,60 400,50 Q500,40 600,50 Q700,60 800,50" dur="3s" repeatCount="indefinite" />
      </path>
      {/* Submarine */}
      <g transform={`translate(300, ${80 + depth})`}>
        {/* Hull */}
        <ellipse cx="100" cy="0" rx="120" ry="35" fill="#37474f" stroke="#455a64" strokeWidth="2" />
        {/* Conning tower */}
        <rect x="70" y="-55" width="50" height="25" rx="6" fill="#455a64" />
        {/* Periscope */}
        <line x1="95" y1="-55" x2="95" y2="-80" stroke="#bdbdbd" strokeWidth="3" />
        <rect x="88" y="-85" width="14" height="8" rx="2" fill="#bdbdbd" />
        {/* Portholes with glow */}
        {[40, 100, 160].map((x) => (
          <circle key={x} cx={x} cy="0" r="10" fill="#42a5f5" opacity="0.3" stroke="#455a64" strokeWidth="2">
            <animate attributeName="opacity" values="0.3;0.5;0.3" dur="2s" repeatCount="indefinite" />
          </circle>
        ))}
        {/* Propeller */}
        <g transform={`translate(-20, 0)`}>
          <animateTransform attributeName="transform" type="rotate" from="0 -20 0" to="360 -20 0" dur="0.5s" repeatCount="indefinite" />
          {[0, 90, 180, 270].map((a) => {
            const rad = a * Math.PI / 180;
            return <ellipse key={a} cx={-20 + Math.cos(rad) * 12} cy={Math.sin(rad) * 12} rx="4" ry="10" fill="#bdbdbd" opacity="0.6" transform={`rotate(${a}, ${-20 + Math.cos(rad) * 12}, ${Math.sin(rad) * 12})`} />;
          })}
          <circle cx="-20" cy="0" r="5" fill="#546e7a" />
        </g>
        {/* Bubbles */}
        {[0, 1, 2, 3].map((i) => (
          <circle key={i} cx={-30 - i * 10} cy={-10 + i * 5} r={3 + i} fill="#90caf9" opacity="0.2">
            <animate attributeName="cy" from={-10 + i * 5} to={-80 - i * 10} dur={`${2 + i}s`} repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.2;0" dur={`${2 + i}s`} repeatCount="indefinite" />
          </circle>
        ))}
      </g>
      {/* Depth scale */}
      {[0, 100, 200, 300].map((d) => (
        <text key={d} x="740" y={70 + d * 1.2} fill="#546e7a" fontSize="10">{d}m</text>
      ))}
      <text x="400" y="570" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 6. Helicopter Rotor ─────────────────────────────────── */
export const HelicopterRotor: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Helicopter";
  const rotation = (props.rotation as number) || 0;
  const phase = (props.phase as number) || 0;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        <linearGradient id="hr-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#42a5f5" /><stop offset="100%" stopColor="#90caf9" />
        </linearGradient>
      </defs>
      <rect width="800" height="600" fill="url(#hr-sky)" rx="16" />
      <g transform={`translate(400, ${280 + Math.sin(phase) * 8})`}>
        {/* Body */}
        <ellipse cx="0" cy="0" rx="80" ry="35" fill="#37474f" stroke="#455a64" strokeWidth="2" />
        {/* Cockpit */}
        <path d="M60,-10 Q90,-15 90,10 Q90,20 60,10" fill="#42a5f5" opacity="0.4" />
        {/* Tail boom */}
        <line x1="-80" y1="0" x2="-200" y2="-20" stroke="#455a64" strokeWidth="8" />
        {/* Tail rotor */}
        <g transform={`rotate(${rotation * 3}, -200, -20)`}>
          <line x1="-200" y1="-40" x2="-200" y2="0" stroke="#bdbdbd" strokeWidth="3" />
          <line x1="-215" y1="-20" x2="-185" y2="-20" stroke="#bdbdbd" strokeWidth="3" />
        </g>
        {/* Main rotor mast */}
        <rect x="-4" y="-45" width="8" height="12" fill="#546e7a" />
        {/* Main rotor blades */}
        <g transform={`rotate(${rotation}, 0, -45)`}>
          {[0, 90, 180, 270].map((a) => (
            <rect key={a} x="-5" y="-45" width="10" height="150"
              rx="4" fill="#bdbdbd" opacity="0.5"
              transform={`rotate(${a}, 0, -45)`} />
          ))}
        </g>
        {/* Landing skids */}
        <line x1="-50" y1="35" x2="-50" y2="55" stroke="#546e7a" strokeWidth="3" />
        <line x1="50" y1="35" x2="50" y2="55" stroke="#546e7a" strokeWidth="3" />
        <line x1="-70" y1="55" x2="70" y2="55" stroke="#546e7a" strokeWidth="4" />
      </g>
      {/* Downwash lines */}
      {[-80, -30, 20, 70].map((x, i) => (
        <line key={i} x1={400 + x} y1={340 + Math.sin(phase) * 8} x2={400 + x * 1.5} y2={480}
          stroke="#ecf0f1" strokeWidth="1" opacity="0.1" strokeDasharray="6,8">
          <animate attributeName="opacity" values="0.1;0.05;0.1" dur="1s" repeatCount="indefinite" begin={`${i * 0.15}s`} />
        </line>
      ))}
      <text x="400" y="545" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 7. Sailboat & Wind ──────────────────────────────────── */
export const SailboatWind: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Sailing";
  const phase = (props.phase as number) || 0;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        <linearGradient id="sb-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#42a5f5" /><stop offset="60%" stopColor="#90caf9" />
          <stop offset="60%" stopColor="#1565c0" /><stop offset="100%" stopColor="#0d47a1" />
        </linearGradient>
      </defs>
      <rect width="800" height="600" fill="url(#sb-sky)" rx="16" />
      {/* Waves */}
      {[0, 1, 2].map((i) => (
        <path key={i} d={`M0,${380 + i * 30} Q100,${370 + i * 30} 200,${380 + i * 30} Q300,${390 + i * 30} 400,${380 + i * 30} Q500,${370 + i * 30} 600,${380 + i * 30} Q700,${390 + i * 30} 800,${380 + i * 30}`}
          fill="none" stroke="#42a5f5" strokeWidth="2" opacity={0.2 - i * 0.05}>
          <animate attributeName="d"
            values={`M0,${380 + i * 30} Q100,${370 + i * 30} 200,${380 + i * 30} Q300,${390 + i * 30} 400,${380 + i * 30} Q500,${370 + i * 30} 600,${380 + i * 30} Q700,${390 + i * 30} 800,${380 + i * 30};M0,${380 + i * 30} Q100,${390 + i * 30} 200,${380 + i * 30} Q300,${370 + i * 30} 400,${380 + i * 30} Q500,${390 + i * 30} 600,${380 + i * 30} Q700,${370 + i * 30} 800,${380 + i * 30};M0,${380 + i * 30} Q100,${370 + i * 30} 200,${380 + i * 30} Q300,${390 + i * 30} 400,${380 + i * 30} Q500,${370 + i * 30} 600,${380 + i * 30} Q700,${390 + i * 30} 800,${380 + i * 30}`}
            dur={`${3 + i}s`} repeatCount="indefinite" />
        </path>
      ))}
      {/* Boat body */}
      <g transform={`translate(400, ${365 + Math.sin(phase * 0.5) * 5}) rotate(${Math.sin(phase * 0.3) * 3})`}>
        <path d="M-80,10 L-60,-10 L60,-10 L80,10 Q0,30 -80,10" fill="#5d4037" stroke="#4e342e" strokeWidth="2" />
        {/* Mast */}
        <line x1="0" y1="-10" x2="0" y2="-180" stroke="#8d6e63" strokeWidth="4" />
        {/* Sail */}
        <path d={`M0,-170 Q${40 + Math.sin(phase) * 15},-100 60,-15 L0,-15 Z`}
          fill="#ecf0f1" opacity="0.8" stroke="#bdbdbd" strokeWidth="1" />
        {/* Flag */}
        <path d={`M0,-170 L${20 + Math.sin(phase * 2) * 8},-180 L0,-190`} fill="#e74c3c" />
      </g>
      {/* Wind arrows */}
      {[120, 200, 280].map((y, i) => (
        <g key={i} opacity="0.3">
          <line x1={150 - i * 20} y1={y} x2={280 - i * 20} y2={y}
            stroke="#ecf0f1" strokeWidth="1.5" markerEnd="url(#sb-wind)">
            <animate attributeName="x1" from={150 - i * 20} to={250 - i * 20} dur="2s" repeatCount="indefinite" />
            <animate attributeName="x2" from={280 - i * 20} to={380 - i * 20} dur="2s" repeatCount="indefinite" />
          </line>
        </g>
      ))}
      <defs>
        <marker id="sb-wind" viewBox="0 0 10 10" refX="10" refY="5" markerWidth="5" markerHeight="5" orient="auto">
          <path d="M0,0 L10,5 L0,10Z" fill="#ecf0f1" />
        </marker>
      </defs>
      <text x="400" y="530" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 8. Hot Air Balloon ──────────────────────────────────── */
export const HotAirBalloon: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Hot Air Balloon";
  const animationProgress = (props.animationProgress as number) || 0;
  const altitude = animationProgress * 200;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        <linearGradient id="hab-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0d47a1" /><stop offset="100%" stopColor="#90caf9" />
        </linearGradient>
        <radialGradient id="hab-env" cx="50%" cy="30%" r="50%">
          <stop offset="0%" stopColor="#e74c3c" /><stop offset="50%" stopColor="#f39c12" /><stop offset="100%" stopColor="#e74c3c" />
        </radialGradient>
      </defs>
      <rect width="800" height="600" fill="url(#hab-sky)" rx="16" />
      {/* Ground */}
      <rect x="0" y="500" width="800" height="100" fill="#2e7d32" opacity="0.4" />
      {/* Balloon */}
      <g transform={`translate(400, ${380 - altitude})`}>
        {/* Envelope */}
        <ellipse cx="0" cy="-90" rx="90" ry="110" fill="url(#hab-env)" opacity="0.85" />
        {/* Vertical stripes */}
        {[-40, 0, 40].map((x) => (
          <line key={x} x1={x} y1="-195" x2={x * 1.1} y2="10" stroke="#e65100" strokeWidth="2" opacity="0.3" />
        ))}
        {/* Ropes */}
        {[-30, -10, 10, 30].map((x) => (
          <line key={x} x1={x * 0.8} y1="15" x2={x * 0.6} y2="60" stroke="#8d6e63" strokeWidth="1" />
        ))}
        {/* Basket */}
        <rect x="-25" y="60" width="50" height="35" rx="4" fill="#6d4c41" stroke="#5d4037" strokeWidth="2" />
        {/* Flame */}
        <path d="M0,20 Q-8,5 0,-10 Q8,5 0,20" fill="#ff9800" opacity="0.8">
          <animate attributeName="d" values="M0,20 Q-8,5 0,-10 Q8,5 0,20;M0,20 Q-10,0 0,-15 Q10,0 0,20;M0,20 Q-8,5 0,-10 Q8,5 0,20" dur="0.4s" repeatCount="indefinite" />
        </path>
      </g>
      {/* Birds */}
      {[200, 350].map((x, i) => (
        <g key={i}>
          <path d={`M${x},${200 - i * 40} Q${x + 10},${190 - i * 40} ${x + 20},${200 - i * 40}`} fill="none" stroke="#1c2833" strokeWidth="1.5">
            <animate attributeName="d" values={`M${x},${200 - i * 40} Q${x + 10},${190 - i * 40} ${x + 20},${200 - i * 40};M${x},${200 - i * 40} Q${x + 10},${195 - i * 40} ${x + 20},${200 - i * 40};M${x},${200 - i * 40} Q${x + 10},${190 - i * 40} ${x + 20},${200 - i * 40}`} dur="0.8s" repeatCount="indefinite" />
          </path>
        </g>
      ))}
      <text x="400" y="560" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};
